import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { useDebounce } from '../useDebounce';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { changeOrderEndpoints } from '@/lib/api/endpoints';
import { changeOrderKeys, jobKeys } from '@/lib/queryKeys';
import type {
  ChangeOrder,
  ChangeOrderImpactPreview,
  ChangeOrderLineItem,
  ChangeOrderListParams,
  ChangeOrderListResponse,
  ChangeOrderScope,
  ChangeOrderStatus,
  CreateChangeOrderDto,
  JobScopeHistory,
  LineItemModification,
  PriceImpact,
  UpdateChangeOrderDto,
} from '@/types/changeOrder';

interface OperationsListResponse {
  data: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
  total_pages?: number;
  hasMore?: boolean;
  has_more?: boolean;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function pick<T = unknown>(record: Record<string, unknown>, camel: string, snake?: string): T {
  return (record[camel] ?? (snake ? record[snake] : undefined)) as T;
}

function asNumber(value: unknown, fallback = 0): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function asLineItems(value: unknown): ChangeOrderLineItem[] {
  return Array.isArray(value) ? (value as ChangeOrderLineItem[]) : [];
}

function asModifiedItems(value: unknown): LineItemModification[] {
  return Array.isArray(value) ? (value as LineItemModification[]) : [];
}

function normalizePriceImpact(value: unknown): PriceImpact {
  const raw = asRecord(value);
  return {
    subtotalDelta: asNumber(pick(raw, 'subtotalDelta', 'subtotal_delta')),
    taxDelta: asNumber(pick(raw, 'taxDelta', 'tax_delta')),
    totalDelta: asNumber(pick(raw, 'totalDelta', 'total_delta')),
  };
}

export function normalizeChangeOrder(raw: Record<string, unknown>): ChangeOrder {
  return {
    id: asString(raw.id),
    tenantId: pick<string | undefined>(raw, 'tenantId', 'tenant_id'),
    jobId: asString(pick(raw, 'jobId', 'job_id')),
    changeOrderNumber: asNumber(pick(raw, 'changeOrderNumber', 'change_order_number')),
    status: asString(raw.status, 'DRAFT') as ChangeOrderStatus,
    description: asString(raw.description),
    reason: asString(raw.reason),
    approvalMethod: pick(raw, 'approvalMethod', 'approval_method'),
    approvalSignatureUrl: pick(raw, 'approvalSignatureUrl', 'approval_signature_url'),
    requestedBy: pick<string | null | undefined>(raw, 'requestedBy', 'requested_by'),
    requestedByName: pick<string | null | undefined>(raw, 'requestedByName', 'requested_by_name'),
    approvedBy: pick<string | null | undefined>(raw, 'approvedBy', 'approved_by'),
    approvedByName: pick<string | null | undefined>(raw, 'approvedByName', 'approved_by_name'),
    approvedAt: pick<string | null | undefined>(raw, 'approvedAt', 'approved_at'),
    rejectionReason: pick<string | null | undefined>(raw, 'rejectionReason', 'rejection_reason'),
    rejectedBy: pick<string | null | undefined>(raw, 'rejectedBy', 'rejected_by'),
    rejectedByName: pick<string | null | undefined>(raw, 'rejectedByName', 'rejected_by_name'),
    rejectedAt: pick<string | null | undefined>(raw, 'rejectedAt', 'rejected_at'),
    lineItemsAdded: asLineItems(pick(raw, 'lineItemsAdded', 'line_items_added')),
    lineItemsRemoved: asStringArray(pick(raw, 'lineItemsRemoved', 'line_items_removed')),
    lineItemsRemovedSnapshot: asLineItems(
      pick(raw, 'lineItemsRemovedSnapshot', 'line_items_removed_snapshot'),
    ),
    lineItemsModified: asModifiedItems(pick(raw, 'lineItemsModified', 'line_items_modified')),
    priceImpact: normalizePriceImpact(pick(raw, 'priceImpact', 'price_impact')),
    appliedAt: pick<string | null | undefined>(raw, 'appliedAt', 'applied_at'),
    createdAt: asString(pick(raw, 'createdAt', 'created_at')),
    updatedAt: asString(pick(raw, 'updatedAt', 'updated_at')),
  };
}

function normalizeChangeOrderList(raw: OperationsListResponse): ChangeOrderListResponse {
  return {
    data: raw.data.map(normalizeChangeOrder),
    total: raw.total,
    page: raw.page,
    limit: raw.limit,
    totalPages: raw.totalPages ?? raw.total_pages,
    hasMore: raw.hasMore ?? raw.has_more,
  };
}

function normalizeScopeHistory(raw: Record<string, unknown>): JobScopeHistory {
  const changeOrders = Array.isArray(raw.changeOrders) ? raw.changeOrders : [];

  return {
    jobId: asString(raw.jobId),
    source: raw.source === 'ESTIMATE' ? 'ESTIMATE' : 'JOB',
    estimateId: pick<string | null | undefined>(raw, 'estimateId', 'estimate_id') ?? null,
    estimateNumber:
      pick<string | null | undefined>(raw, 'estimateNumber', 'estimate_number') ?? null,
    convertedAt: pick<string | null | undefined>(raw, 'convertedAt', 'converted_at') ?? null,
    originalLineItems: asLineItems(pick(raw, 'originalLineItems', 'original_line_items')),
    currentLineItems: asLineItems(pick(raw, 'currentLineItems', 'current_line_items')),
    changeOrders: changeOrders.map((entry) => {
      const record = asRecord(entry);
      return {
        id: asString(record.id),
        changeOrderNumber: asNumber(pick(record, 'changeOrderNumber', 'change_order_number')),
        status: asString(record.status, 'DRAFT') as ChangeOrderStatus,
        description: asString(record.description),
        reason: asString(record.reason),
        priceImpact: normalizePriceImpact(pick(record, 'priceImpact', 'price_impact')),
        lineItemsAdded: asLineItems(pick(record, 'lineItemsAdded', 'line_items_added')),
        lineItemsRemoved: asStringArray(pick(record, 'lineItemsRemoved', 'line_items_removed')),
        lineItemsRemovedSnapshot: asLineItems(
          pick(record, 'lineItemsRemovedSnapshot', 'line_items_removed_snapshot'),
        ),
        lineItemsModified: asModifiedItems(
          pick(record, 'lineItemsModified', 'line_items_modified'),
        ),
        createdAt: asString(pick(record, 'createdAt', 'created_at')),
        approvedAt: pick<string | null | undefined>(record, 'approvedAt', 'approved_at'),
        appliedAt: pick<string | null | undefined>(record, 'appliedAt', 'applied_at'),
      };
    }),
  };
}

function isChangeOrderListResponse(value: unknown): value is ChangeOrderListResponse {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    Array.isArray((value as ChangeOrderListResponse).data)
  );
}

function statusMatchesParams(changeOrder: ChangeOrder, params?: ChangeOrderListParams) {
  return !params?.status || params.status === changeOrder.status;
}

function mergeChangeOrderIntoList(
  list: ChangeOrderListResponse,
  changeOrder: ChangeOrder,
  params?: ChangeOrderListParams,
): ChangeOrderListResponse {
  const index = list.data.findIndex((item) => item.id === changeOrder.id);
  const shouldInclude = statusMatchesParams(changeOrder, params);

  if (!shouldInclude) {
    if (index < 0) return list;
    return {
      ...list,
      data: list.data.filter((item) => item.id !== changeOrder.id),
      total: Math.max(0, list.total - 1),
    };
  }

  if (index >= 0) {
    return {
      ...list,
      data: list.data.map((item) => (item.id === changeOrder.id ? changeOrder : item)),
    };
  }

  return {
    ...list,
    data: [changeOrder, ...list.data],
    total: list.total + 1,
  };
}

export function useJobChangeOrders(jobId: string, params: ChangeOrderListParams = {}) {
  return useApiQuery<ChangeOrderListResponse>({
    queryKey: changeOrderKeys.byJob(jobId, params),
    queryFn: async () => {
      const raw = await apiGet<OperationsListResponse>(
        operationsClient,
        changeOrderEndpoints.listByJob(jobId),
        { params },
      );
      return normalizeChangeOrderList(raw);
    },
    enabled: Boolean(jobId),
    showErrorToast: false,
  });
}

/**
 * The change order an editor holds, priced by the server (NOT-784): the very
 * figures a save would store, tax and discounts included. The editor used to
 * add up its own line amounts instead, and quoted +$100.00 for a change the
 * save stored as +$108.75.
 *
 * Debounced so typing a quantity does not price every keystroke, and the last
 * answer stays on screen while the next one loads — `isFetching` covers the
 * debounce too, so a caller can tell the figures are catching up.
 */
export function useChangeOrderImpactPreview(
  jobId: string,
  scope: ChangeOrderScope,
  enabled = true,
) {
  const scopeKey = JSON.stringify(scope);
  const debouncedScopeKey = useDebounce(scopeKey, 300);

  const query = useApiQuery<ChangeOrderImpactPreview>({
    queryKey: changeOrderKeys.impactPreview(jobId, debouncedScopeKey),
    queryFn: ({ signal }) =>
      apiPost<ChangeOrderImpactPreview, ChangeOrderScope>(
        operationsClient,
        changeOrderEndpoints.preview(jobId),
        JSON.parse(debouncedScopeKey) as ChangeOrderScope,
        { signal },
      ),
    enabled: enabled && Boolean(jobId),
    showErrorToast: false,
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    isFetching: query.isFetching || scopeKey !== debouncedScopeKey,
  };
}

export function useJobScopeHistory(jobId: string) {
  return useApiQuery<JobScopeHistory>({
    queryKey: changeOrderKeys.scopeHistory(jobId),
    queryFn: () =>
      apiGet<Record<string, unknown>>(
        operationsClient,
        changeOrderEndpoints.scopeHistory(jobId),
      ).then(normalizeScopeHistory),
    enabled: Boolean(jobId),
    showErrorToast: false,
  });
}

export function useChangeOrder(id: string) {
  return useApiQuery<ChangeOrder>({
    queryKey: changeOrderKeys.detail(id),
    queryFn: () =>
      apiGet<Record<string, unknown>>(operationsClient, changeOrderEndpoints.detail(id)).then(
        normalizeChangeOrder,
      ),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

function useChangeOrderInvalidation(jobId?: string) {
  const queryClient = useQueryClient();

  return async (changeOrder?: ChangeOrder) => {
    if (changeOrder) {
      queryClient.setQueryData(changeOrderKeys.detail(changeOrder.id), changeOrder);
    }

    const affectedJobId = changeOrder?.jobId ?? jobId;
    if (affectedJobId) {
      const listQueryKey = [...changeOrderKeys.all(), 'job', affectedJobId] as const;
      for (const [queryKey, list] of queryClient.getQueriesData<ChangeOrderListResponse>({
        queryKey: listQueryKey,
      })) {
        if (!isChangeOrderListResponse(list) || !changeOrder) continue;
        const params = Array.isArray(queryKey)
          ? (queryKey[4] as ChangeOrderListParams | undefined)
          : undefined;
        queryClient.setQueryData(queryKey, mergeChangeOrderIntoList(list, changeOrder, params));
      }
    }

    await queryClient.invalidateQueries({ queryKey: changeOrderKeys.all() });
    if (affectedJobId) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(affectedJobId) }),
        queryClient.invalidateQueries({ queryKey: changeOrderKeys.scopeHistory(affectedJobId) }),
      ]);
    }
  };
}

export function useCreateChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<ChangeOrder, CreateChangeOrderDto>({
    mutationFn: ({ jobId, ...dto }) =>
      apiPost<Record<string, unknown>, Omit<CreateChangeOrderDto, 'jobId'>>(
        operationsClient,
        changeOrderEndpoints.create(jobId),
        dto,
      ).then(normalizeChangeOrder),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
    },
  });
}

export function useUpdateChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<ChangeOrder, UpdateChangeOrderDto>({
    mutationFn: ({ id, jobId: _jobId, ...dto }) =>
      apiPatch<Record<string, unknown>, Omit<UpdateChangeOrderDto, 'id' | 'jobId'>>(
        operationsClient,
        changeOrderEndpoints.update(id),
        dto,
      ).then(normalizeChangeOrder),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
      toast.success('Change order updated');
    },
  });
}

export function useSubmitChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<ChangeOrder, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<Record<string, unknown>>(operationsClient, changeOrderEndpoints.submit(id)).then(
        normalizeChangeOrder,
      ),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
    },
  });
}

export function useSendChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<
    ChangeOrder & { delivery: Record<string, unknown> },
    { id: string; channels?: ('email' | 'sms')[] }
  >({
    mutationFn: ({ id, channels }) =>
      apiPost<Record<string, unknown>, { channels?: ('email' | 'sms')[] }>(
        operationsClient,
        changeOrderEndpoints.send(id),
        { channels },
      ).then((raw) => ({ ...normalizeChangeOrder(raw), delivery: asRecord(raw.delivery) })),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
      const sent = asStringArray(changeOrder.delivery.sentChannels);
      const failed = asStringArray(changeOrder.delivery.failedChannels);
      if (!sent.length) {
        toast.warning(
          'Change order saved, but delivery was not confirmed. Check customer contact details and communication settings, then resend from the job.',
        );
      } else if (failed.length) {
        toast.warning(
          `Change order sent by ${sent.join(' and ')}, but not by ${failed.join(' or ')}. You can resend from the job.`,
        );
      } else {
        toast.success('Change order sent to customer');
      }
    },
  });
}

export function useApproveChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<ChangeOrder, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<Record<string, unknown>>(operationsClient, changeOrderEndpoints.approve(id)).then(
        normalizeChangeOrder,
      ),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
    },
  });
}

export function useRejectChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<ChangeOrder, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) =>
      apiPost<Record<string, unknown>, { reason?: string }>(
        operationsClient,
        changeOrderEndpoints.reject(id),
        { reason },
      ).then(normalizeChangeOrder),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
    },
  });
}

export function useApplyChangeOrder() {
  const invalidate = useChangeOrderInvalidation();

  return useApiMutation<ChangeOrder, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<Record<string, unknown>>(operationsClient, changeOrderEndpoints.apply(id)).then(
        normalizeChangeOrder,
      ),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
    },
  });
}

export function useVoidChangeOrder(jobId: string) {
  const invalidate = useChangeOrderInvalidation(jobId);

  return useApiMutation<ChangeOrder, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<Record<string, unknown>>(operationsClient, changeOrderEndpoints.void(jobId, id)).then(
        normalizeChangeOrder,
      ),
    onSuccess: async (changeOrder) => {
      await invalidate(changeOrder);
    },
  });
}
